import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@/store/userStore';
import { useAuth } from '@/auth/AuthContext';
import { apiService, PRO_PLAN, MAX_PLAN } from '@/api';
import { isPaidPlan } from '@/utils/planHelpers';
import { Button, Input, Textarea, Modal } from '@/components/ui';
import { cn } from '@/utils/cn';
import {
  Check, Sparkles, Download, Film,
  AlertCircle, CreditCard, Lock, Share2,
  CheckCircle, Plus, Minus,
  Eye, Shield, HelpCircle, ArrowLeft, Mail,
  Volume2, VolumeX, ChevronDown, ChevronUp, X,
  Star, Calendar, ArrowUpRight, Building2,
  Loader2, Send
} from 'lucide-react';
import {
  trackPaywallShown,
  trackBeginCheckout,
  trackPurchase,
  trackCheckoutCanceled,
  trackCancelReasonSelected,
  trackCreditsPurchase,
  trackBillingPeriodToggle,
} from '@/services/analyticsService';
import { useDirectPayment, clearDirectPaymentFailureFlag } from '@/components/shared/EmbeddedCheckoutModal';

const CREDITS_PER_PACK = 10;
const PRICE_PER_PACK = 2;
const CREDIT_STEP = 50;
const MIN_CREDITS = 50;
const MAX_CREDITS = 100000;
const DEFAULT_CREDITS = 200;

const generateTransactionId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

type PlanType = 'pro' | 'max';
type CancelReason = 'browsing' | 'trust' | 'info' | null;

const PLAN_CONFIGS = { pro: PRO_PLAN, max: MAX_PLAN } as const;

const RECOVERY_VIDEO_EXAMPLES = [
  { id: 'figure-skater-cat', videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/triple_axel.mp4', promptKey: 'examples.prompts.figureSkaterCat' },
  { id: 'astronaut-dog', videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/20250922_1557_an%20astronaut%20golden%20retriever%20named%20_Sora_%20levitates%20around%20an%20intergalactic%20pup-themed%20space%20statio_simple_compose_01k5sta9hmegr9axrpbty5sxva%20(1).mp4', promptKey: 'examples.prompts.astronautDog' },
  { id: 'anime-fireworks', videoUrl: 'https://openaiassets.blob.core.windows.net/$web/nf2/blog-final/golden/6eda9a57-5d6d-4890-90ee-61f89e999719/anime_fireworks%20(1).mp4', promptKey: 'examples.prompts.animeFireworks' },
  { id: 'cyberpunk-skater', videoUrl: 'https://v3.fal.media/files/panda/476McAVlSQMqM7lXptiMc_output.mp4', promptKey: 'examples.prompts.cyberpunkSkater' },
];


function RecoveryVideoCard({ example }: { example: typeof RECOVERY_VIDEO_EXAMPLES[0] }) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => setIsVisible(entry.isIntersecting)), { threshold: 0.3, rootMargin: '50px' });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;
    if (isVisible) videoRef.current.play().catch(() => {});
    else videoRef.current.pause();
  }, [isVisible]);

  const toggleMute = (e: React.MouseEvent) => { e.stopPropagation(); if (videoRef.current) { videoRef.current.muted = !isMuted; setIsMuted(!isMuted); } };
  const prompt = t(example.promptKey);
  const truncatedPrompt = prompt.length > 100 ? prompt.slice(0, 100) + '...' : prompt;

  return (
    <div ref={containerRef} className="group relative rounded-2xl overflow-hidden bg-surface border border-glass-border hover:border-primary/30 transition-all duration-300">
      <div className="relative aspect-video overflow-hidden bg-black">
        <video ref={videoRef} src={example.videoUrl} className="w-full h-full object-cover" loop muted={isMuted} playsInline preload="metadata" />
        <button onClick={toggleMute} className={cn('absolute bottom-2 right-2 p-2 rounded-full backdrop-blur-md transition-all duration-200 hover:scale-110', isMuted ? 'bg-black/60 text-white/80' : 'bg-primary/80 text-white')}>
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="p-3">
        <p className="text-xs text-text-secondary leading-relaxed">{isExpanded ? prompt : truncatedPrompt}</p>
        {prompt.length > 100 && (
          <button onClick={() => setIsExpanded(!isExpanded)} className="mt-1 flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-light transition-colors">
            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {isExpanded ? t('generator.showLess') : t('generator.showMore')}
          </button>
        )}
      </div>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-glass-border rounded-xl overflow-hidden">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 text-left hover:bg-surface-light/50 transition-colors">
        <span className="text-sm font-medium text-text-primary pr-4">{question}</span>
        <ChevronDown className={cn("w-4 h-4 text-text-tertiary flex-shrink-0 transition-transform duration-200", isOpen && "rotate-180")} />
      </button>
      <div className={cn("overflow-hidden transition-all duration-200", isOpen ? "max-h-48 pb-4 px-4" : "max-h-0")}>
        <p className="text-sm text-text-secondary leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

function AnimatedPrice({ value, className }: { value: number; className?: string }) {
  const [display, setDisplay] = useState(value);
  const animRef = useRef<number>();
  const prevValue = useRef(value);

  useEffect(() => {
    if (prevValue.current === value) return;
    const from = prevValue.current;
    const to = value;
    prevValue.current = value;
    const duration = 350;
    const start = performance.now();
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (to - from) * ease));
      if (t < 1) animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [value]);

  return <span className={className}>${display}</span>;
}

/**
 * Enterprise contact form modal. Posts to /enterprise-contact which forwards
 * the inquiry to support@vivideo.ai via Resend (Reply-To set to the submitter).
 */
type EnterpriseVolume = '1-99' | '100-999' | '1000+';
const ENTERPRISE_VOLUME_OPTIONS: EnterpriseVolume[] = ['1-99', '100-999', '1000+'];

/**
 * Free / personal email providers we reject on the Enterprise form. We want
 * inquiries to come from real company domains so sales can qualify the lead.
 * Kept lowercased; comparison is case-insensitive.
 */
const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.fr', 'yahoo.de', 'yahoo.es', 'yahoo.it', 'yahoo.com.br', 'yahoo.com.mx', 'yahoo.ca', 'ymail.com', 'rocketmail.com',
  'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'hotmail.de', 'hotmail.es', 'hotmail.it',
  'outlook.com', 'outlook.fr', 'outlook.de', 'outlook.es', 'outlook.it', 'outlook.com.br',
  'live.com', 'live.co.uk', 'live.fr', 'live.de', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'aim.com',
  'proton.me', 'protonmail.com', 'pm.me',
  'gmx.com', 'gmx.net', 'gmx.de', 'gmx.us',
  'mail.com', 'email.com',
  'yandex.com', 'yandex.ru', 'ya.ru',
  'zoho.com',
  'mail.ru', 'inbox.ru', 'list.ru', 'bk.ru',
  'qq.com', '163.com', '126.com', 'sina.com', 'sina.cn',
  'naver.com', 'daum.net', 'hanmail.net',
  'web.de', 't-online.de', 'freenet.de',
  'libero.it', 'tiscali.it', 'virgilio.it',
  'orange.fr', 'free.fr', 'sfr.fr', 'laposte.net', 'wanadoo.fr',
  'fastmail.com', 'fastmail.fm',
  'tutanota.com', 'tuta.io',
  'hey.com',
]);

function isFreeEmailDomain(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at === -1) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return FREE_EMAIL_DOMAINS.has(domain);
}

function EnterpriseContactModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [volume, setVolume] = useState<EnterpriseVolume | ''>('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string; volume?: string; message?: string }>({});

  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setSubmitting(false);
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submitting) return;

    const errs: typeof fieldErrors = {};
    const trimmedEmail = email.trim();
    if (!name.trim()) errs.name = t('subscription.enterprise.form.errors.nameRequired');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) {
      errs.email = t('subscription.enterprise.form.errors.invalidEmail');
    } else if (isFreeEmailDomain(trimmedEmail)) {
      errs.email = t('subscription.enterprise.form.errors.useCompanyEmail');
    }
    if (!volume) errs.volume = t('subscription.enterprise.form.errors.volumeRequired');
    if (!message.trim()) errs.message = t('subscription.enterprise.form.errors.messageRequired');
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      await apiService.submitEnterpriseContact({
        name: name.trim(),
        email: email.trim(),
        monthlyVideoCount: volume as EnterpriseVolume,
        message: message.trim(),
        source: 'webapp',
      });
      setSubmitted(true);
      setName('');
      setEmail('');
      setVolume('');
      setMessage('');
    } catch (err) {
      console.error('Enterprise contact submit failed:', err);
      setError(t('subscription.enterprise.form.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={submitted ? undefined : t('subscription.enterprise.form.title')} size="lg">
      {submitted ? (
        <div className="py-6 text-center space-y-4 animate-fade-in">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(16,185,129,0.4)]">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-text-primary mb-1">{t('subscription.enterprise.form.successTitle')}</h3>
            <p className="text-sm text-text-secondary">{t('subscription.enterprise.form.successBody')}</p>
          </div>
          <Button onClick={onClose} variant="secondary" size="md" className="mt-2">
            {t('plan.modal.close', 'Close')}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-text-secondary -mt-2">{t('subscription.enterprise.form.subtitle')}</p>
          <Input
            label={t('subscription.enterprise.form.nameLabel')}
            placeholder={t('subscription.enterprise.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
            disabled={submitting}
            autoComplete="name"
          />
          <Input
            label={t('subscription.enterprise.form.emailLabel')}
            placeholder={t('subscription.enterprise.form.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
            disabled={submitting}
            type="email"
            autoComplete="email"
          />
          <div className="w-full">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('subscription.enterprise.form.volumeLabel')}
            </label>
            <div
              className={cn(
                'grid grid-cols-3 gap-2 p-1 rounded-xl bg-surface-light border',
                fieldErrors.volume ? 'border-status-error' : 'border-glass-border'
              )}
              role="radiogroup"
              aria-label={t('subscription.enterprise.form.volumeLabel')}
            >
              {ENTERPRISE_VOLUME_OPTIONS.map((opt) => {
                const selected = volume === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={submitting}
                    onClick={() => setVolume(opt)}
                    className={cn(
                      'px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                      'focus:outline-none focus:ring-2 focus:ring-primary/50',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      selected
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-secondary hover:bg-surface hover:text-text-primary'
                    )}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {fieldErrors.volume && (
              <p className="mt-1.5 text-sm text-status-error">{fieldErrors.volume}</p>
            )}
          </div>
          <div className="w-full">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('subscription.enterprise.form.messageLabel')}
            </label>
            <Textarea
              placeholder={t('subscription.enterprise.form.messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={submitting}
              className={cn('min-h-[120px]', fieldErrors.message && 'border-status-error focus:ring-status-error/50')}
            />
            {fieldErrors.message && (
              <p className="mt-1.5 text-sm text-status-error">{fieldErrors.message}</p>
            )}
          </div>
          {error && (
            <div className="p-3 rounded-xl bg-status-error/10 border border-status-error/20 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-status-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-status-error">{error}</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onClose}
              disabled={submitting}
              className="sm:flex-1"
            >
              {t('subscription.enterprise.form.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              isLoading={submitting}
              className="sm:flex-[2] bg-primary hover:bg-primary/90"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {submitting ? t('subscription.enterprise.form.submitting') : t('subscription.enterprise.form.submit')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function SubscriptionPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userId: storeUserId, refreshUser, setUserId } = useUserStore();
  const { firebaseUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<PlanType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [creditAmount, setCreditAmount] = useState(DEFAULT_CREDITS);
  const [cancelReason, setCancelReason] = useState<CancelReason>(null);
  const { openPayment, paymentModal } = useDirectPayment();
  const hasTrackedPurchase = useRef(false);
  const hasTrackedPaywallView = useRef(false);
  const [trialActivating, setTrialActivating] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);
  const hasTrialActivated = useRef(false);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);

  const isPro = isPaidPlan(user?.userPlan);
  const showCreditPurchase = isPro;
  const isCanceled = searchParams.get('canceled') === 'true';
  const canceledPlan = (searchParams.get('plan') as PlanType) || 'pro';
  const [billingPeriod, setBillingPeriod] = useState<'weekly' | 'yearly'>(() => {
    if (isPro && user?.priceType === 'weekly') return 'weekly';
    return 'yearly';
  });


  const totalPrice = (creditAmount / CREDITS_PER_PACK) * PRICE_PER_PACK;

  const PLAN_BENEFITS = (credits: number, videoCount: number): { node: React.ReactNode; large?: boolean }[] => [
    { node: <><span className="font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{credits}</span> {t('subscription.creditsPerWeek', { count: credits }).replace(String(credits), '').trim()} {t('subscription.upToVideos', { count: videoCount })}</>, large: true },
    { node: t('subscription.hdQualityExports') },
    { node: t('subscription.noWatermarks') },
    { node: t('subscription.downloadShareVideos') },
    { node: t('subscription.extendVideo') },
    { node: t('subscription.editWithPrompt') },
    { node: t('subscription.videoRemix') },
    { node: t('subscription.accessAllFeatures') },
  ];

  const CONTEXT_MESSAGES: Record<string, { icon: typeof CreditCard; title: string; message: string; color: string; bgColor: string; borderColor: string }> = {
    insufficient_credits: { icon: CreditCard, title: t('subscription.insufficientCredits'), message: t('subscription.needMoreCreditsMessage'), color: 'text-amber-500', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30' },
    pro_feature: { icon: Lock, title: t('subscription.featureLocked'), message: t('subscription.upgradeToAccess'), color: 'text-primary', bgColor: 'bg-primary/10', borderColor: 'border-primary/30' },
    download: { icon: Download, title: t('subscription.upgradeToProDownload'), message: t('subscription.downloadShareVideos'), color: 'text-accent-green', bgColor: 'bg-accent-green/10', borderColor: 'border-accent-green/30' },
    share: { icon: Share2, title: t('subscription.upgradeToProShare'), message: t('subscription.downloadShareVideos'), color: 'text-accent-blue', bgColor: 'bg-accent-blue/10', borderColor: 'border-accent-blue/30' },
    upgrade_for_full_video: { icon: Film, title: t('subscription.upgradeToWatchFullVideo') || 'Upgrade to Watch Full Video', message: t('subscription.upgradeToWatchFullVideoMessage') || 'Your video preview is ready! Upgrade to Pro to generate and watch the full video.', color: 'text-primary', bgColor: 'bg-gradient-to-r from-primary/10 to-accent/10', borderColor: 'border-primary/50' },
  };

  // --- Trial activation effect ---
  useEffect(() => {
    const isTrial = searchParams.get('trial') === 'true';
    const sessionId = searchParams.get('session_id');
    const transactionId = searchParams.get('txn_id');
    if (!isTrial || !sessionId) return;
    if (hasTrialActivated.current) return;

    const trialKey = transactionId ? `vivideo_trial_${transactionId}` : `vivideo_trial_${sessionId}`;
    if (sessionStorage.getItem(trialKey)) {
      setShowSuccessMessage(true);
      refreshUser().then(() => setTimeout(() => navigate('/'), 2000));
      return;
    }

    hasTrialActivated.current = true;
    setTrialActivating(true);
    setTrialError(null);

    if (!storeUserId) {
      setTrialActivating(false);
      setTrialError('User not found. Please log in and try again.');
      return;
    }

    apiService.activateTrial(sessionId, storeUserId)
      .then(() => {
        sessionStorage.setItem(trialKey, 'true');
        setTrialActivating(false);
        setShowSuccessMessage(true);
        if (transactionId) {
          trackPurchase({ planType: 'pro_yearly_trial', value: 0, isSubscription: true, transactionId, metaEvent: 'StartTrial' });
        }
        refreshUser().then(() => setTimeout(() => navigate('/'), 2000));
      })
      .catch((err) => {
        setTrialActivating(false);
        const errorData = err?.response?.data;
        if (errorData?.error === 'credit_card_required') {
          setTrialError(t('subscription.creditCardRequired') || 'Only credit cards are accepted for the free trial. Please use a credit card.');
        } else if (errorData?.error === 'prepaid_card_rejected') {
          setTrialError(t('subscription.prepaidCardRejected') || 'Prepaid cards are not accepted. Please try with another card. We suggest using a credit card.');
        } else if (errorData?.error === 'card_verification_failed') {
          setTrialError(errorData.message || 'Card verification failed. Please use a card with sufficient funds.');
        } else {
          setTrialError('Something went wrong. Please try again.');
        }
        console.error('Trial activation error:', err);
      });
  }, [searchParams, storeUserId, refreshUser, navigate]);

  // --- Effects ---
  // Return from setup-mode "update card" Checkout — adopt PM, then reopen yearly confirm.
  // Webhook may already have adopted; don't block the confirm modal on adopt failure.
  useEffect(() => {
    if (searchParams.get('update_card') !== 'true') return;
    const sessionId = searchParams.get('session_id');
    const priceId = searchParams.get('price_id');
    if (!storeUserId || !sessionId) return;
    navigate('/subscription', { replace: true });
    void (async () => {
      try {
        await apiService.adoptPaymentMethod(storeUserId, sessionId);
      } catch (err) {
        console.warn('adoptPaymentMethod failed (webhook may have already adopted):', err);
      }
      try {
        await refreshUser();
        if (priceId) {
          const cfg = user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN;
          openPayment({
            type: 'upgrade',
            label: t('subscription.switchYearlySave', { amount: cfg.weeklyAmount - cfg.yearlyPerWeek }),
            description: `$${cfg.yearlyPerWeek}${t('subscription.perWeek')}`,
            amount: `$${cfg.yearlyAmount}/${t('subscription.billingToggle.yearly').toLowerCase()}`,
            priceId,
          });
        }
      } catch (err) {
        console.error('Update card return error:', err);
        setError(t('errors.somethingWentWrong'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, storeUserId]);

  useEffect(() => {
    const success = searchParams.get('success');
    if (success !== 'true') return;
    if (searchParams.get('trial') === 'true') return;
    const purchasedCredits = searchParams.get('credits');
    if (purchasedCredits) clearDirectPaymentFailureFlag();
    const transactionId = searchParams.get('txn_id');
    if (!transactionId) { setShowSuccessMessage(true); refreshUser().then(() => setTimeout(() => navigate('/'), 2000)); return; }
    const purchaseKey = `vivideo_tracked_${transactionId}`;
    if (sessionStorage.getItem(purchaseKey)) { setShowSuccessMessage(true); refreshUser().then(() => setTimeout(() => navigate('/'), 2000)); return; }
    if (hasTrackedPurchase.current) return;
    sessionStorage.setItem(purchaseKey, 'true');
    hasTrackedPurchase.current = true;
    setShowSuccessMessage(true);
    const purchasedPlan = (searchParams.get('plan') as PlanType) || 'pro';
    const purchasedPeriod = (searchParams.get('period') as 'weekly' | 'yearly') || 'yearly';
    const planConfig = PLAN_CONFIGS[purchasedPlan];
    if (purchasedCredits) { const ca = parseInt(purchasedCredits, 10); trackCreditsPurchase(ca, (ca / CREDITS_PER_PACK) * PRICE_PER_PACK, transactionId); }
    else { trackPurchase({ planType: `${purchasedPlan}_${purchasedPeriod}`, value: purchasedPeriod === 'yearly' ? planConfig.yearlyAmount : planConfig.weeklyAmount, isSubscription: true, transactionId }); }
    refreshUser().then(() => setTimeout(() => navigate('/'), 2000));
  }, [searchParams, refreshUser, navigate]);

  // Pricing-page paywall impression — the SINGLE canonical `paywall_shown` for
  // this surface (the old inline fires at trigger sites are now
  // `paywall_cta_clicked`, so an encounter is no longer counted twice). Fires
  // once per mount for ANY arrival ('direct' when no ?context=), except Stripe
  // round-trip returns. The context banner stays gated to known contexts.
  useEffect(() => {
    const ctx = searchParams.get('context');
    if (ctx && CONTEXT_MESSAGES[ctx]) setContext(ctx);
    if (hasTrackedPaywallView.current) return;
    if (searchParams.get('success') === 'true' || searchParams.get('canceled') === 'true' || searchParams.get('trial') === 'true') return;
    hasTrackedPaywallView.current = true;
    trackPaywallShown(ctx || 'direct');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => { if (isCanceled && !showCreditPurchase) trackCheckoutCanceled(); }, [isCanceled, showCreditPurchase]);
  useEffect(() => { if (cancelReason) trackCancelReasonSelected(cancelReason); }, [cancelReason]);

  // --- Handlers ---
  const handleSubscribe = async (plan: PlanType, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!firebaseUser || isLoading) return;
    setIsLoading(true); setLoadingPlan(plan); setError(null);
    try {
      const { userId: freshUserId } = await apiService.getUserById(firebaseUser.email || '', firebaseUser.uid);
      setUserId(freshUserId);
      const config = PLAN_CONFIGS[plan];
      const priceId = billingPeriod === 'yearly' ? config.yearlyPriceId : config.weeklyPriceId;
      const price = billingPeriod === 'yearly' ? config.yearlyAmount : config.weeklyAmount;
      trackBeginCheckout(`${plan}_${billingPeriod}`, price);
      const txnId = generateTransactionId();
      const { checkoutUrl } = await apiService.createCheckoutSession(freshUserId, billingPeriod, firebaseUser.email || user?.email || undefined, undefined, `${window.location.origin}/subscription?success=true&txn_id=${txnId}&plan=${plan}&period=${billingPeriod}`, `${window.location.origin}/subscription?canceled=true&plan=${plan}`, false, priceId);
      if (checkoutUrl) window.location.href = checkoutUrl;
    } catch (err) { setError(t('errors.somethingWentWrong')); console.error(err); }
    finally { setIsLoading(false); setLoadingPlan(null); }
  };

  const handleBuyCredits = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!firebaseUser || isLoading) return;
    trackBeginCheckout('credits', totalPrice);
    openPayment({
      type: 'credits',
      label: t('subscription.buyAdditionalCredits'),
      description: `${creditAmount} ${t('account.credits').toLowerCase()}`,
      amount: `$${totalPrice.toFixed(2)}`,
      creditAmount,
    });
  };


  const handleCreditChange = (value: number) => setCreditAmount(Math.round(Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, value)) / CREDIT_STEP) * CREDIT_STEP);
  const handleReadyToSubscribe = async (plan: PlanType = canceledPlan) => { await handleSubscribe(plan); };
  const handleCloseModal = () => { navigate('/subscription', { replace: true }); setCancelReason(null); };


  const contextMessage = context ? CONTEXT_MESSAGES[context] : null;
  const creditsPurchased = searchParams.get('credits');

  // --- Trial activation screen ---
  if (trialActivating) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(99,102,241,0.4)]">
            <CreditCard className="w-12 h-12 text-white animate-pulse" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-3">{t('subscription.verifyingCard') || 'Verifying your card...'}</h1>
          <p className="text-text-secondary mb-2">{t('subscription.verifyingCardDesc') || 'A temporary $1 hold is being placed to verify your card. It will be released immediately.'}</p>
          <div className="mt-6 flex justify-center"><div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" /></div>
        </div>
      </div>
    );
  }

  if (trialError) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(239,68,68,0.4)]">
            <AlertCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-3">{t('subscription.cardVerificationFailed') || 'Card Verification Failed'}</h1>
          <p className="text-text-secondary mb-6">{trialError}</p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
            onClick={() => { setTrialError(null); navigate('/subscription', { replace: true }); }}
          >
            {t('subscription.startCreating')}
          </Button>
        </div>
      </div>
    );
  }

  // --- Success screen ---
  if (showSuccessMessage) {
    const purchasedPlan = searchParams.get('plan') as PlanType | null;
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-accent-green to-emerald-500 flex items-center justify-center mx-auto mb-6 shadow-[0_0_40px_rgba(34,197,94,0.4)]"><CheckCircle className="w-12 h-12 text-white" /></div>
          <h1 className="text-3xl font-bold text-text-primary mb-3">{t('common.success')}!</h1>
          <p className="text-text-secondary mb-2">{creditsPurchased ? t('subscription.creditsPurchaseSuccess', { amount: creditsPurchased }) : `${purchasedPlan === 'max' ? t('subscription.max') : t('subscription.pro')} ${t('common.success').toLowerCase()}`}</p>
          <p className="text-sm text-text-tertiary">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // --- Recovery modal ---
  const renderRecoveryModal = () => {
    if (!isCanceled || showCreditPurchase) return null;
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4 p-6 md:p-8 rounded-3xl bg-surface border border-glass-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <button onClick={handleCloseModal} className="absolute top-4 left-4 p-2 rounded-full bg-surface-light border border-glass-border hover:bg-glass-border transition-colors z-20 animate-fade-in" aria-label="Close"><X className="w-5 h-5 text-text-secondary" /></button>
          {!cancelReason ? (
            <>
              <div className="text-center mb-10"><h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">{t('subscription.canceledTitle')}</h1><p className="text-lg text-text-secondary">{t('subscription.canceledSubtitle')}</p></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <button onClick={() => setCancelReason('browsing')} className="group p-6 rounded-2xl bg-surface-light border-2 border-glass-border hover:border-primary/50 transition-all duration-300 text-left"><div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><Eye className="w-7 h-7 text-primary" /></div><h3 className="text-lg font-semibold text-text-primary mb-2">{t('subscription.reasonBrowsing')}</h3><p className="text-sm text-text-secondary">{t('subscription.reasonBrowsingDesc')}</p></button>
                <button onClick={() => setCancelReason('trust')} className="group p-6 rounded-2xl bg-surface-light border-2 border-glass-border hover:border-emerald-500/50 transition-all duration-300 text-left"><div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4"><Shield className="w-7 h-7 text-emerald-500" /></div><h3 className="text-lg font-semibold text-text-primary mb-2">{t('subscription.reasonTrust')}</h3><p className="text-sm text-text-secondary">{t('subscription.reasonTrustDesc')}</p></button>
                <button onClick={() => setCancelReason('info')} className="group p-6 rounded-2xl bg-surface-light border-2 border-glass-border hover:border-amber-500/50 transition-all duration-300 text-left"><div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4"><HelpCircle className="w-7 h-7 text-amber-500" /></div><h3 className="text-lg font-semibold text-text-primary mb-2">{t('subscription.reasonInfo')}</h3><p className="text-sm text-text-secondary">{t('subscription.reasonInfoDesc')}</p></button>
              </div>
              <div className="text-center"><Button variant="ghost" onClick={() => handleReadyToSubscribe()} className="text-text-secondary hover:text-text-primary">{t('subscription.backToSubscribe')}</Button></div>
            </>
          ) : (
            <>
              <button onClick={() => setCancelReason(null)} className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-6 transition-colors"><ArrowLeft className="w-5 h-5" /><span>{t('common.goBack')}</span></button>
              {cancelReason === 'browsing' && (<><div className="text-center mb-8"><h2 className="text-2xl font-bold text-text-primary mb-2">{t('examples.title')}</h2><p className="text-text-secondary">{t('examples.subtitle')}</p></div><div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">{RECOVERY_VIDEO_EXAMPLES.map((ex) => <RecoveryVideoCard key={ex.id} example={ex} />)}</div></>)}
              {cancelReason === 'trust' && (<><div className="text-center mb-8"><h2 className="text-2xl font-bold text-text-primary mb-2">{t('subscription.reasonTrust')}</h2></div><div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"><div className="p-6 rounded-2xl bg-surface-light border border-glass-border"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 flex items-center justify-center"><svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" fill="#635BFF"/></svg></div><div><h3 className="font-semibold text-text-primary">{t('subscription.securePayment')}</h3><p className="text-sm text-text-secondary">{t('subscription.securePaymentDesc')}</p></div></div></div><div className="p-6 rounded-2xl bg-surface-light border border-glass-border"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center"><Check className="w-7 h-7 text-emerald-500" /></div><div><h3 className="font-semibold text-text-primary">{t('subscription.cancelAnytimeTitle')}</h3><p className="text-sm text-text-secondary">{t('subscription.cancelAnytimeDesc')}</p></div></div></div><div className="p-6 rounded-2xl bg-surface-light border border-glass-border"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><CreditCard className="w-7 h-7 text-primary" /></div><div><h3 className="font-semibold text-text-primary">{t('subscription.noHiddenFees')}</h3><p className="text-sm text-text-secondary">{t('subscription.noHiddenFeesDesc')}</p></div></div></div><div className="p-6 rounded-2xl bg-surface-light border border-glass-border"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center"><Mail className="w-7 h-7 text-amber-500" /></div><div><h3 className="font-semibold text-text-primary">{t('subscription.reachUs')}</h3><a href="mailto:support@vivideo.ai" className="text-sm text-primary hover:underline">support@vivideo.ai</a></div></div></div></div></>)}
              {cancelReason === 'info' && (<><div className="text-center mb-8"><h2 className="text-2xl font-bold text-text-primary mb-2">{t('subscription.accessAllFeatures')}</h2></div><div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">{RECOVERY_VIDEO_EXAMPLES.slice(0, 2).map((ex) => <RecoveryVideoCard key={ex.id} example={ex} />)}</div></>)}
              <Button size="lg" className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 shadow-[0_0_30px_rgba(16,185,129,0.5)] text-lg py-6" onClick={() => handleReadyToSubscribe()}><Check className="w-5 h-5 mr-2" />{t('subscription.backToSubscribe')}</Button>
            </>
          )}
        </div>
      </div>
    );
  };

  // --- Paid user UI: credit purchase + yearly push for weekly subs ---
  if (showCreditPurchase) {
    const currentPlanConfig = user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN;
    const isWeeklySubscriber = user?.priceType === 'weekly';

    const handleSwitchToYearly = () => {
      openPayment({
        type: 'upgrade',
        label: t('subscription.switchYearlySave', { amount: currentPlanConfig.weeklyAmount - currentPlanConfig.yearlyPerWeek }),
        description: `$${currentPlanConfig.yearlyPerWeek}${t('subscription.perWeek')}`,
        amount: `$${currentPlanConfig.yearlyAmount}/${t('subscription.billingToggle.yearly').toLowerCase()}`,
        priceId: currentPlanConfig.yearlyPriceId,
      });
    };

    return (
      <div className="max-w-4xl mx-auto animate-fade-in">
        {isWeeklySubscriber && (
          <div className="mb-6 p-5 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-green-500/5 cursor-pointer hover:border-emerald-500/50 transition-all" onClick={handleSwitchToYearly}>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-emerald-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-text-primary mb-0.5">{t('subscription.switchYearlySave', { amount: currentPlanConfig.weeklyAmount - currentPlanConfig.yearlyPerWeek })}</h3>
                <p className="text-xs text-text-secondary">${currentPlanConfig.yearlyPerWeek}{t('subscription.perWeek')} — {t('subscription.billedYearly', { price: `$${currentPlanConfig.yearlyAmount}` })}</p>
              </div>
              <ArrowUpRight className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            </div>
          </div>
        )}

        {error && (<div className="mb-6 p-4 bg-status-error/10 border border-status-error/20 rounded-xl flex items-center gap-3"><AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" /><p className="text-status-error">{error}</p></div>)}

        <div className="text-center mb-6"><h2 className="text-2xl font-bold text-text-primary mb-1">{t('subscription.buyAdditionalCredits')}</h2><p className="text-sm text-text-secondary">{t('subscription.selectCreditsAmount')}</p></div>
        <div className="mb-6 p-6 rounded-2xl bg-surface border border-glass-border">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-6 px-8 py-5 rounded-2xl bg-surface-light border border-primary/30">
              <button onClick={(e) => { e.stopPropagation(); handleCreditChange(creditAmount - CREDIT_STEP); }} disabled={creditAmount <= MIN_CREDITS} className="w-12 h-12 rounded-full bg-surface border border-glass-border flex items-center justify-center hover:bg-surface-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><Minus className="w-5 h-5 text-text-secondary" /></button>
              <div className="min-w-[140px] text-center"><input type="number" value={creditAmount} onChange={(e) => handleCreditChange(parseInt(e.target.value) || MIN_CREDITS)} onClick={(e) => e.stopPropagation()} min={MIN_CREDITS} max={MAX_CREDITS} step={CREDIT_STEP} className="w-28 text-4xl font-bold text-text-primary bg-transparent text-center border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /><p className="text-sm text-text-secondary mt-1">{t('account.credits').toLowerCase()}</p></div>
              <button onClick={(e) => { e.stopPropagation(); handleCreditChange(creditAmount + CREDIT_STEP); }} disabled={creditAmount >= MAX_CREDITS} className="w-12 h-12 rounded-full bg-surface border border-glass-border flex items-center justify-center hover:bg-surface-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"><Plus className="w-5 h-5 text-text-secondary" /></button>
            </div>
          </div>
          <div className="text-center p-4 rounded-xl bg-primary/10 border border-primary/30">
            <p className="text-text-secondary text-sm mb-1">{t('subscription.totalPrice')}</p>
            <p className="text-3xl font-bold text-primary">${totalPrice.toFixed(2)}</p>
            <p className="text-xs text-text-tertiary mt-1">${PRICE_PER_PACK} / {CREDITS_PER_PACK} {t('account.credits').toLowerCase()}</p>
          </div>
        </div>
        <Button size="lg" className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-[0_0_20px_rgba(139,92,246,0.3)] text-lg py-6" onClick={handleBuyCredits} isLoading={isLoading}><CreditCard className="w-5 h-5 mr-2" />{t('subscription.buyCredits')} - ${totalPrice.toFixed(2)}</Button>
        <div className="mt-6 text-center"><p className="text-xs text-text-tertiary"><a href="https://vivideo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-secondary">{t('about.termsOfService')}</a>{' & '}<a href="https://vivideo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-secondary">{t('about.privacyPolicy')}</a></p></div>
        {paymentModal}
      </div>
    );
  }

  // ======================================================
  // FREE/EXPIRED USERS — PLAN SELECTION
  // ======================================================

  const proPrice = billingPeriod === 'yearly' ? PRO_PLAN.yearlyPerWeek : PRO_PLAN.weeklyAmount;
  const maxPrice = billingPeriod === 'yearly' ? MAX_PLAN.yearlyPerWeek : MAX_PLAN.weeklyAmount;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-8">
      <div className="pb-12 pt-4 px-0">
        {/* Context message */}
        {contextMessage && (
          <div className={cn('mb-6 p-4 rounded-2xl border flex items-start gap-4', contextMessage.bgColor, contextMessage.borderColor)}>
            <div className={cn('p-2 rounded-xl', contextMessage.bgColor)}><contextMessage.icon className={cn('w-6 h-6', contextMessage.color)} /></div>
            <div><h3 className={cn('font-semibold', contextMessage.color)}>{contextMessage.title}</h3><p className="text-text-secondary text-sm mt-1">{contextMessage.message}</p></div>
          </div>
        )}

        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 rounded-xl bg-surface border border-glass-border">
            <button
              onClick={() => { if (billingPeriod !== 'weekly') { setBillingPeriod('weekly'); trackBillingPeriodToggle('weekly', 'none'); } }}
              className={cn('px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200', billingPeriod === 'weekly' ? 'bg-surface-light text-text-primary shadow-sm' : 'text-text-tertiary hover:text-text-secondary')}
            >
              {t('subscription.billingToggle.weekly')}
            </button>
            <button
              onClick={() => { if (billingPeriod !== 'yearly') { setBillingPeriod('yearly'); trackBillingPeriodToggle('yearly', 'none'); } }}
              className={cn('relative px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200', billingPeriod === 'yearly' ? 'bg-surface-light text-text-primary shadow-sm' : 'text-text-tertiary hover:text-text-secondary')}
            >
              {t('subscription.billingToggle.yearly')}
              <span className="absolute -top-2.5 -right-2 px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-emerald-500 text-white">
                {t('subscription.savePercent', { percent: 75 })}
              </span>
            </button>
          </div>
        </div>

        {error && (<div className="mb-6 p-4 bg-status-error/10 border border-status-error/20 rounded-xl flex items-center gap-3"><AlertCircle className="w-5 h-5 text-status-error flex-shrink-0" /><p className="text-status-error">{error}</p></div>)}

        {/* ===== PLAN CARDS ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {/* PRO card */}
          <div
            onClick={() => !isLoading && handleSubscribe('pro')}
            className={cn(
              "relative rounded-2xl border bg-surface flex flex-col cursor-pointer transition-all duration-200 hover:border-primary/40 hover:shadow-lg",
              loadingPlan === 'pro' ? "border-primary/50 shadow-lg" : "border-glass-border"
            )}
          >
            <div className="p-6 md:p-8 flex-1 flex flex-col">
              <div className="mb-5">
                <h3 className="text-2xl font-bold text-text-primary mb-1">{t('subscription.pro')}</h3>
                <p className="text-sm text-text-secondary">{t('subscription.proDescription')}</p>
              </div>
              <div className="mb-5">
                <div className="flex items-baseline gap-1.5">
                  <AnimatedPrice value={proPrice} className="text-4xl font-bold text-text-primary" />
                  <span className="text-text-secondary text-sm">{t('subscription.perWeek')}</span>
                </div>
                <div className="h-5 mt-1.5">
                  {billingPeriod === 'weekly' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setBillingPeriod('yearly'); trackBillingPeriodToggle('yearly', 'none'); }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-500 hover:text-emerald-400 underline underline-offset-2 decoration-emerald-500/40 hover:decoration-emerald-400 transition-colors"
                    >
                      <span>{t('subscription.switchYearlySave', { amount: PRO_PLAN.weeklyAmount - PRO_PLAN.yearlyPerWeek })}</span>
                      <ArrowUpRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-text-secondary text-center mb-2">{t('subscription.cancelAnytime')}</p>
              <Button size="lg" className="w-full text-base py-5 mb-1 bg-primary hover:bg-primary/90" onClick={(e) => { e.stopPropagation(); handleSubscribe('pro', e); }} isLoading={isLoading && loadingPlan === 'pro'} disabled={isLoading && loadingPlan !== 'pro'}>{t('subscription.startCreating')}</Button>
              <p className="text-xs text-text-secondary text-center mb-5 h-4">{billingPeriod === 'yearly' ? t('subscription.billedYearly', { price: `$${PRO_PLAN.yearlyAmount}` }) : ''}</p>
              <div className="space-y-2.5 flex-1">
                {PLAN_BENEFITS(PRO_PLAN.credits, 10).map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className={cn("mt-0.5 flex-shrink-0 text-primary", b.large ? "w-5 h-5" : "w-4 h-4")} />
                    <span className={cn(b.large ? "text-base font-semibold text-text-primary" : "text-sm text-text-primary")}>{b.node}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* MAX card */}
          <div
            onClick={() => !isLoading && handleSubscribe('max')}
            className={cn(
              "relative rounded-2xl border-2 bg-surface flex flex-col cursor-pointer transition-all duration-200 hover:shadow-lg",
              loadingPlan === 'max' ? "border-primary/60 shadow-lg" : "border-primary/30 hover:border-primary/50"
            )}
          >
            <div className="absolute -top-3 right-4 z-10">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold shadow-lg"><Sparkles className="w-3 h-3" />{t('subscription.mostPopular')}</span>
            </div>
            <div className="p-6 md:p-8 flex-1 flex flex-col">
              <div className="mb-5">
                <h3 className="text-2xl font-bold text-text-primary mb-1">{t('subscription.max')}</h3>
                <p className="text-sm text-text-secondary">{t('subscription.maxDescription')}</p>
              </div>
              <div className="mb-5">
                <div className="flex items-baseline gap-1.5">
                  <AnimatedPrice value={maxPrice} className="text-4xl font-bold text-text-primary" />
                  <span className="text-text-secondary text-sm">{t('subscription.perWeek')}</span>
                </div>
                <div className="h-5 mt-1.5">
                  {billingPeriod === 'weekly' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setBillingPeriod('yearly'); trackBillingPeriodToggle('yearly', 'none'); }}
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-500 hover:text-emerald-400 underline underline-offset-2 decoration-emerald-500/40 hover:decoration-emerald-400 transition-colors"
                    >
                      <span>{t('subscription.switchYearlySave', { amount: MAX_PLAN.weeklyAmount - MAX_PLAN.yearlyPerWeek })}</span>
                      <ArrowUpRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-text-secondary text-center mb-2">{t('subscription.cancelAnytime')}</p>
              <Button size="lg" className="w-full text-base py-5 mb-1 bg-primary hover:bg-primary/90" onClick={(e) => { e.stopPropagation(); handleSubscribe('max', e); }} isLoading={isLoading && loadingPlan === 'max'} disabled={isLoading && loadingPlan !== 'max'}>{t('subscription.startCreating')}</Button>
              <p className="text-xs text-text-secondary text-center mb-5 h-4">{billingPeriod === 'yearly' ? t('subscription.billedYearly', { price: `$${MAX_PLAN.yearlyAmount}` }) : ''}</p>
              <div className="space-y-2.5 flex-1">
                {PLAN_BENEFITS(MAX_PLAN.credits, 33).map((b, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className={cn("mt-0.5 flex-shrink-0 text-primary", b.large ? "w-5 h-5" : "w-4 h-4")} />
                    <span className={cn(b.large ? "text-base font-semibold text-text-primary" : "text-sm text-text-primary")}>{b.node}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise banner */}
        <div className="mb-10 relative rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-4 md:gap-6 p-4 md:p-5">
            <div>
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-text-primary leading-tight">{t('subscription.enterprise.title')}</h3>
                  <p className="text-xs text-text-secondary">{t('subscription.enterprise.subtitle')}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
                {[
                  'unlimitedCredits',
                  'teamSeats',
                  'apiAccess',
                  'sso',
                  'customIntegrations',
                  'prioritySupport',
                  'sla',
                  'customInvoicing',
                ].map((key) => (
                  <div key={key} className="flex items-start gap-2">
                    <Check className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 text-primary" />
                    <span className="text-xs text-text-primary leading-snug">{t(`subscription.enterprise.features.${key}`)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center justify-center text-center md:border-l md:border-glass-border md:pl-6">
              <div className="text-2xl font-bold text-text-primary leading-tight">{t('subscription.enterprise.priceLabel')}</div>
              <p className="text-[11px] text-text-tertiary mb-3">{t('subscription.enterprise.priceSubLabel')}</p>
              <Button
                size="md"
                className="w-full bg-primary hover:bg-primary/90"
                onClick={() => {
                  trackBeginCheckout('enterprise_contact', 0);
                  setEnterpriseOpen(true);
                }}
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('subscription.enterprise.cta')}
              </Button>
              <p className="text-[11px] text-text-tertiary mt-2">{t('subscription.enterprise.replyTime')}</p>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-surface border border-glass-border"><Shield className="w-5 h-5 text-primary flex-shrink-0" /><div><p className="text-sm font-medium text-text-primary">{t('subscription.securePayment')}</p><p className="text-xs text-text-tertiary">{t('subscription.securePaymentDesc')}</p></div></div>
          <div className="flex items-center gap-3 p-4 rounded-xl bg-surface border border-glass-border"><Check className="w-5 h-5 text-primary flex-shrink-0" /><div><p className="text-sm font-medium text-text-primary">{t('subscription.cancelAnytime')}</p><p className="text-xs text-text-tertiary">{t('subscription.cancelAnytimeDescription')}</p></div></div>
          <div className="flex items-center gap-3 p-4 rounded-xl bg-surface border border-glass-border"><CreditCard className="w-5 h-5 text-primary flex-shrink-0" /><div><p className="text-sm font-medium text-text-primary">{t('subscription.noHiddenFees')}</p><p className="text-xs text-text-tertiary">{t('subscription.noHiddenFeesDesc')}</p></div></div>
        </div>

        {/* Reviews */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-text-primary mb-5 text-center">{t('subscription.whatCreatorsSay')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[{ n: 1, avatar: 'https://i.pravatar.cc/80?img=32' }, { n: 2, avatar: 'https://i.pravatar.cc/80?img=12' }, { n: 3, avatar: 'https://i.pravatar.cc/80?img=47' }].map(({ n, avatar }) => (
              <div key={n} className="p-5 rounded-xl bg-surface border border-glass-border">
                <div className="flex items-center gap-3 mb-3"><img src={avatar} alt="" className="w-10 h-10 rounded-full object-cover" /><div><p className="text-sm font-medium text-text-primary">{t(`subscription.review${n}Author`)}</p><p className="text-xs text-text-tertiary">{t(`subscription.review${n}Role`)}</p></div></div>
                <div className="flex gap-0.5 mb-2">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />)}</div>
                <p className="text-sm text-text-secondary leading-relaxed">&ldquo;{t(`subscription.review${n}`)}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-text-primary mb-4 text-center">{t('subscription.faq')}</h2>
          <div className="space-y-2 max-w-2xl mx-auto">
            <FaqItem question={t('subscription.faqCreditsQ')} answer={t('subscription.faqCreditsA')} />
            <FaqItem question={t('subscription.faqCancelQ')} answer={t('subscription.faqCancelA')} />
            <FaqItem question={t('subscription.faqDiffQ')} answer={t('subscription.faqDiffA')} />
            <FaqItem question={t('subscription.faqPaymentQ')} answer={t('subscription.faqPaymentA')} />
            <FaqItem question={t('subscription.faqPaymentFailQ')} answer={t('subscription.faqPaymentFailA')} />
            <FaqItem question={t('subscription.faqRefundQ')} answer={t('subscription.faqRefundA')} />
          </div>
        </div>

        {/* Terms */}
        <div className="text-center">
          <p className="text-xs text-text-tertiary">
            <a href="https://vivideo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-secondary">{t('about.termsOfService')}</a>
            {' & '}
            <a href="https://vivideo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-text-secondary">{t('about.privacyPolicy')}</a>
          </p>
        </div>
      </div>

      {renderRecoveryModal()}
      {paymentModal}
      <EnterpriseContactModal isOpen={enterpriseOpen} onClose={() => setEnterpriseOpen(false)} />
    </div>
  );
}
